/**
 * Model for creating new AI prompt configurations.
 */
export type AIPromptConfigCreate = {
	/**
	 * Version identifier (simple, standard, enhanced)
	 */
	version: string;
	/**
	 * Model identifier or raw provider model string from Agenta
	 */
	model: string;
	/**
	 * Optional agent identifier (e.g., emma_timeline_analyzer)
	 */
	agent_key?: string | null;
	/**
	 * Maximum tokens to generate
	 */
	max_tokens?: number;
	/**
	 * Sampling temperature
	 */
	temperature?: number;
	/**
	 * Nucleus sampling parameter (1.0 uses full probability mass)
	 */
	top_p?: number;
	/**
	 * Optional top-k sampling limit; null defers to provider default
	 */
	top_k?: number | null;
	/**
	 * Penalty applied to repeated tokens
	 */
	frequency_penalty?: number;
	/**
	 * Penalty encouraging topic diversity
	 */
	presence_penalty?: number;
	/**
	 * Optional list of stop sequences that end generation early
	 */
	stop_sequences?: Array<string>;
	/**
	 * System prompt that defines the AI behavior and output format
	 */
	system_prompt: string;
	/**
	 * Optional JSON schema for validating structured model output
	 */
	output_schema_json?: Record<string, unknown> | null;
	/**
	 * Whether this configuration is currently active
	 */
	is_active?: boolean;
	/**
	 * Agenta app slug for provenance tracking
	 */
	agenta_app_slug?: string | null;
	/**
	 * Agenta variant slug for provenance tracking
	 */
	agenta_variant_slug?: string | null;
	/**
	 * Agenta version number for provenance tracking
	 */
	agenta_version_number?: number | null;
};



/**
 * Public model for AI prompt configurations.
 */
export type AIPromptConfigPublic = {
	/**
	 * Version identifier (simple, standard, enhanced)
	 */
	version: string;
	/**
	 * Model identifier or raw provider model string from Agenta
	 */
	model: string;
	/**
	 * Optional agent identifier (e.g., emma_timeline_analyzer)
	 */
	agent_key?: string | null;
	/**
	 * Maximum tokens to generate
	 */
	max_tokens?: number;
	/**
	 * Sampling temperature
	 */
	temperature?: number;
	/**
	 * Nucleus sampling parameter (1.0 uses full probability mass)
	 */
	top_p?: number;
	/**
	 * Optional top-k sampling limit; null defers to provider default
	 */
	top_k?: number | null;
	/**
	 * Penalty applied to repeated tokens
	 */
	frequency_penalty?: number;
	/**
	 * Penalty encouraging topic diversity
	 */
	presence_penalty?: number;
	/**
	 * Optional list of stop sequences that end generation early
	 */
	stop_sequences?: Array<string>;
	/**
	 * System prompt that defines the AI behavior and output format
	 */
	system_prompt: string;
	/**
	 * Optional JSON schema for validating structured model output
	 */
	output_schema_json?: Record<string, unknown> | null;
	/**
	 * Whether this configuration is currently active
	 */
	is_active?: boolean;
	/**
	 * Agenta app slug for provenance tracking
	 */
	agenta_app_slug?: string | null;
	/**
	 * Agenta variant slug for provenance tracking
	 */
	agenta_variant_slug?: string | null;
	/**
	 * Agenta version number for provenance tracking
	 */
	agenta_version_number?: number | null;
	id: number;
	created_datetime: string;
	updated_datetime: string;
};



/**
 * Model for updating AI prompt configurations.
 */
export type AIPromptConfigUpdate = {
	model?: string | null;
	max_tokens?: number | null;
	temperature?: number | null;
	top_p?: number | null;
	top_k?: number | null;
	frequency_penalty?: number | null;
	presence_penalty?: number | null;
	stop_sequences?: Array<string> | null;
	system_prompt?: string | null;
	output_schema_json?: Record<string, unknown> | null;
	is_active?: boolean | null;
	agenta_app_slug?: string | null;
	agenta_variant_slug?: string | null;
	agenta_version_number?: number | null;
};



/**
 * Public model for list of AI prompt configurations.
 */
export type AIPromptConfigsPublic = {
	data: Array<AIPromptConfigPublic>;
	count: number;
};



export type AccessRequestData = {
	name: string;
	email: string;
};



export type ActiveBrandBody = {
	activeBrand: 'wysehub' | 'aiclaims';
};




export type AgentRunPublic = {
	agentName: string;
	runType: string;
	/**
	 * pending, in_progress, success, failure
	 */
	status?: string;
	referralId?: number | null;
	claimId?: number | null;
	userId?: number | null;
	taskId?: string | null;
	model?: string | null;
	version?: string | null;
	cacheHit?: boolean | null;
	cacheStatus?: string | null;
	cacheCreationInputTokens?: number | null;
	cacheReadInputTokens?: number | null;
	cacheablePrefixSize?: number | null;
	cacheCheckpointsCount?: number | null;
	durationSeconds?: number | null;
	inputTokens?: number | null;
	outputTokens?: number | null;
	totalTokens?: number | null;
	totalApiCost?: number | null;
	startedAt?: string;
	completedAt?: string | null;
	errorMessage?: string | null;
	metadata?: Record<string, unknown> | null;
	tenantId?: string | null;
	workflowId?: number | null;
	/**
	 * Values: postgres, agenta
	 */
	promptSource?: string | null;
	promptAppSlug?: string | null;
	promptVariantSlug?: string | null;
	promptEnvironment?: string | null;
	promptVersionNumber?: number | null;
	promptVersionId?: string | null;
	agentaTraceId?: string | null;
	agentaSpanId?: string | null;
	evaluationMode?: boolean;
	evaluationRunId?: string | null;
	comparisonGroupId?: string | null;
	id: string;
	created_datetime: string;
	updated_datetime: string | null;
};



export type AgentRunSummary = {
	total: number;
	breakdown: Array<AgentRunSummaryItem>;
};



export type AgentRunSummaryItem = {
	agent_name: string;
	status: string;
	count: number;
};



export type AgentRunsPublic = {
	data: Array<AgentRunPublic>;
	count: number;
};



/**
 * Represents an Agenta app with slug and name.
 */
export type AgentaApp = {
	slug: string;
	name: string;
};



/**
 * Response containing list of available Agenta apps.
 */
export type AgentaAppsResponse = {
	apps: Array<AgentaApp>;
	count: number;
};



/**
 * Request body for pulling a prompt from Agenta.
 */
export type AgentaPullRequest = {
	app_slug: string;
	environment_slug?: string;
	variant_slug?: string | null;
};



export type AiClaimsDashboardSummaryPublic = {
	workflowsConfigured: number;
	workflowsActive: number;
	runsTotal: number;
	runsFailed: number;
	totalCost: number;
	successRatePct: number;
};



export type AnnieCreate = {
	accident_circs?: string | null;
	recordingUrl?: string | null;
};



export type Annie_Result = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	circs_grade_id?: number | null;
	accident_circs?: string | null;
	claim_id?: number | null;
	fault_probability?: number | null;
	nonfault_probability?: number | null;
	split_probability?: number | null;
	trigger?: string | null;
	probability?: number | null;
	adjustedliabscore?: number | null;
	id?: number | null;
};



export type ApiKeyIssueRequest = {
	partnerId?: number | null;
	name?: string | null;
	scopes?: Record<string, unknown> | null;
	expiresDatetime?: string | null;
};



export type ApiKeyMetadataPublic = {
	id: number;
	userId?: number | null;
	partnerId?: number | null;
	createdByUserId?: number | null;
	name?: string | null;
	status?: string | null;
	scopes?: Record<string, unknown> | null;
	expiresDatetime?: string | null;
	lastUsedDatetime?: string | null;
	revokedReason?: string | null;
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
};



export type ApiKeyMetadataUpdateRequest = {
	name?: string | null;
	scopes?: Record<string, unknown> | null;
	expiresDatetime?: string | null;
	status?: string | null;
	revokedReason?: string | null;
};



export type ApiKeysMetadataPublic = {
	data: Array<ApiKeyMetadataPublic>;
	count: number;
};



/**
 * Model for creating new application settings.
 */
export type AppSettingCreate = {
	/**
	 * Setting key (e.g., RATELIMITER_MAX_ATTEMPTS)
	 */
	key: string;
	/**
	 * Setting value as string (type-safe via value_type)
	 */
	value: string;
	/**
	 * Value type: string, int, bool, float, json
	 */
	value_type?: string;
	/**
	 * Setting category: rate_limiting, ai_models, email, storage, logging, features, etc.
	 */
	category: string;
	/**
	 * Target environment: local, staging, production, or all
	 */
	environment: string;
	/**
	 * Whether this setting is active
	 */
	is_active?: boolean;
	/**
	 * Human-readable description of the setting
	 */
	description?: string | null;
};



/**
 * Public model for application settings.
 */
export type AppSettingPublic = {
	/**
	 * Setting key (e.g., RATELIMITER_MAX_ATTEMPTS)
	 */
	key: string;
	/**
	 * Setting value as string (type-safe via value_type)
	 */
	value: string;
	/**
	 * Value type: string, int, bool, float, json
	 */
	value_type?: string;
	/**
	 * Setting category: rate_limiting, ai_models, email, storage, logging, features, etc.
	 */
	category: string;
	/**
	 * Target environment: local, staging, production, or all
	 */
	environment: string;
	/**
	 * Whether this setting is active
	 */
	is_active?: boolean;
	/**
	 * Human-readable description of the setting
	 */
	description?: string | null;
	id: number;
	created_datetime: string;
	updated_datetime: string;
};



/**
 * Model for updating application settings.
 */
export type AppSettingUpdate = {
	value?: string | null;
	value_type?: string | null;
	category?: string | null;
	environment?: string | null;
	is_active?: boolean | null;
	description?: string | null;
};



/**
 * Public model for list of application settings.
 */
export type AppSettingsPublic = {
	data: Array<AppSettingPublic>;
	count: number;
};



export type AuthoritiesPublic = {
	data: Array<AuthorityPublic>;
	count: number;
};



export type AuthorityCreate = {
	name?: string | null;
	type?: string | null;
};



export type AuthorityPublic = {
	name?: string | null;
	type?: string | null;
	id: number;
};



export type AuthorityUpdate = {
	name?: string | null;
	type?: string | null;
};



export type Body_calls_incoming_call = {
	From: string;
};



export type Body_edge_import_upload_edge_csv = {
	partner_id: number;
	file: Blob | File;
};



export type Body_login_login_access_token = {
	grant_type?: string | null;
	username: string;
	password: string;
	scope?: string;
	client_id?: string | null;
	client_secret?: string | null;
};



export type Body_newrefparser_parse_email = {
	file: Blob | File;
};



export type Body_referrals_create_bulk_summary_operation = {
	/**
	 * List of allocation statuses to filter referrals
	 */
	allocation_statuses: Array<string>;
	/**
	 * AI model to use (sonnet, sonnet46, or sonnet46_cached)
	 */
	model?: string;
	/**
	 * Summary length (simple, standard, or enhanced)
	 */
	version?: string;
	/**
	 * Task priority (0-9, higher is more urgent)
	 */
	priority?: number;
};



export type Body_requests_request_upload_csv = {
	csv_file: Blob | File;
};



export type Body_requests_request_upload_csv_no_partner_rules = {
	file: Blob | File;
};



export type Body_uploads_upload_files = {
	referral_id: number;
	files: Array<Blob | File>;
};



export type Body_utils_sms_handler = {
	From: string;
	Body: string;
};



/**
 * Model for creating new bulk operation configurations.
 */
export type BulkOperationConfigCreate = {
	/**
	 * Configuration name (default, admin, etc.)
	 */
	config_name?: string;
	/**
	 * Maximum operations per hour per user
	 */
	max_operations_per_hour?: number;
	/**
	 * Maximum concurrent operations system-wide
	 */
	max_concurrent_operations?: number;
	/**
	 * Operation timeout in hours
	 */
	operation_timeout_hours?: number;
	/**
	 * Role-specific bulk operation limits
	 */
	role_overrides?: Record<string, unknown> | null;
	/**
	 * Whether bulk operation limits are enabled
	 */
	enabled?: boolean;
	/**
	 * Description of the bulk operation limits
	 */
	description?: string | null;
};



/**
 * Public model for bulk operation configurations.
 */
export type BulkOperationConfigPublic = {
	/**
	 * Configuration name (default, admin, etc.)
	 */
	config_name?: string;
	/**
	 * Maximum operations per hour per user
	 */
	max_operations_per_hour?: number;
	/**
	 * Maximum concurrent operations system-wide
	 */
	max_concurrent_operations?: number;
	/**
	 * Operation timeout in hours
	 */
	operation_timeout_hours?: number;
	/**
	 * Role-specific bulk operation limits
	 */
	role_overrides?: Record<string, unknown> | null;
	/**
	 * Whether bulk operation limits are enabled
	 */
	enabled?: boolean;
	/**
	 * Description of the bulk operation limits
	 */
	description?: string | null;
	id: number;
	created_datetime: string;
	updated_datetime: string;
};



/**
 * Model for updating bulk operation configurations.
 */
export type BulkOperationConfigUpdate = {
	max_operations_per_hour?: number | null;
	max_concurrent_operations?: number | null;
	operation_timeout_hours?: number | null;
	role_overrides?: Record<string, unknown> | null;
	enabled?: boolean | null;
	description?: string | null;
};



/**
 * Public model for list of bulk operation configurations.
 */
export type BulkOperationConfigsPublic = {
	data: Array<BulkOperationConfigPublic>;
	count: number;
};



export type BulkOperationPublic = {
	id: number;
	user_id: number;
	status: string;
	total_referrals: number;
	processed_count: number;
	successful_count: number;
	failed_count: number;
	configuration: Record<string, unknown> | null;
	task_id: string | null;
	started_at: string | null;
	completed_at: string | null;
	created_datetime: string;
	updated_datetime: string;
	progress_percentage: number;
	errors?: Record<string, unknown> | null;
};



export type Circs_Grade = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	grade: string;
	desc: string;
	id?: number | null;
};



export type Circs_GradeCreate = {
	grade: string;
	desc: string;
};



export type Circs_GradePublic = {
	grade: string;
	desc: string;
	id: number;
};



export type Circs_GradeUpdate = {
	grade?: string | null;
	desc?: string | null;
};



export type Circs_GradesPublic = {
	data: Array<Circs_GradePublic>;
	count: number;
};



export type Claim = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	client_firstname: string;
	client_lastname: string;
	client_address_1?: string | null;
	client_address_town?: string | null;
	client_address_postcode?: string | null;
	client_vehicle_reg?: string | null;
	client_insurer?: string | null;
	client_insurer_id?: number | null;
	client_insurer_policyno?: string | null;
	client_phone?: string | null;
	client_mobile?: string | null;
	client_email?: string | null;
	authority_id?: number | null;
	plate_type?: string | null;
	accident_date?: string | null;
	accident_time?: string | null;
	accident_location?: string | null;
	accident_circs?: string | null;
	tp_firstname?: string | null;
	tp_lastname?: string | null;
	tp_address_1?: string | null;
	tp_address_town?: string | null;
	tp_address_postcode?: string | null;
	tp_vehicle_reg?: string | null;
	tp_insurer?: string | null;
	tp_insurer_id?: number | null;
	tp_insurer_polcyno?: string | null;
	tp_phone?: string | null;
	tp_mobile?: string | null;
	referral_id?: number | null;
	circs_grade_id?: number | null;
	/**
	 * Client's date of birth
	 */
	client_dob?: string | null;
	/**
	 * Client's occupation
	 */
	client_occupation?: string | null;
	/**
	 * Purpose of journey
	 */
	purpose_of_journey?: string | null;
	/**
	 * Pre-existing damage
	 */
	pre_existing_damage?: string | null;
	/**
	 * Description of client's damage
	 */
	client_damage_description?: string | null;
	/**
	 * Number of passengers Clients
	 */
	no_of_passengers_clients?: number | null;
	/**
	 * Client's speed
	 */
	client_speed?: string | null;
	/**
	 * Were there any witnesses
	 */
	any_witnesses?: boolean | null;
	/**
	 * Weather conditions
	 */
	weather_conditions?: string | null;
	/**
	 * Source of liability
	 */
	source_liability?: string | null;
	/**
	 * Emergency service attended
	 */
	emergency_service_attend?: boolean | null;
	/**
	 * Did police attend
	 */
	police_attended?: boolean | null;
	/**
	 * Did fire brigade attend
	 */
	fire_brigade_attended?: boolean | null;
	/**
	 * Did ambulance attend
	 */
	ambulance_attended?: boolean | null;
	/**
	 * Name of Police station
	 */
	police_station_name?: string | null;
	/**
	 * Name of Police officer
	 */
	police_officer_name?: string | null;
	/**
	 * Police Reference
	 */
	police_reference?: string | null;
	/**
	 * Policy Cover
	 */
	policy_cover?: string | null;
	/**
	 * Policy Excess
	 */
	policy_excess?: number | null;
	/**
	 * Policy Start Date
	 */
	policy_start_date?: string | null;
	/**
	 * Policy Expires Date
	 */
	policy_expiry_date?: string | null;
	/**
	 * VAT Registered
	 */
	vat_registered?: boolean | null;
	/**
	 * Policy vehicle use
	 */
	policy_vehicle_use?: string | null;
	/**
	 * Client Vehicle Drivable
	 */
	client_vehicle_drivable?: boolean | null;
	/**
	 * Client Vehicle Secure
	 */
	client_vehicle_secure?: boolean | null;
	/**
	 * Client Vehicle Location
	 */
	client_vehicle_location?: string | null;
	storage?: boolean | null;
	storage_location?: string | null;
	id?: number | null;
};



export type ClaimCreate = {
	client_firstname: string;
	client_lastname: string;
	client_address_1?: string | null;
	client_address_town?: string | null;
	client_address_postcode?: string | null;
	client_vehicle_reg?: string | null;
	client_insurer?: string | null;
	client_insurer_id?: number | null;
	client_insurer_policyno?: string | null;
	client_phone?: string | null;
	client_mobile?: string | null;
	client_email?: string | null;
	authority_id?: number | null;
	plate_type?: string | null;
	accident_date?: string | null;
	accident_time?: string | null;
	accident_location?: string | null;
	accident_circs?: string | null;
	tp_firstname?: string | null;
	tp_lastname?: string | null;
	tp_address_1?: string | null;
	tp_address_town?: string | null;
	tp_address_postcode?: string | null;
	tp_vehicle_reg?: string | null;
	tp_insurer?: string | null;
	tp_insurer_id?: number | null;
	tp_insurer_polcyno?: string | null;
	tp_phone?: string | null;
	tp_mobile?: string | null;
	referral_id?: number | null;
	circs_grade_id?: number | null;
	/**
	 * Client's date of birth
	 */
	client_dob?: string | null;
	/**
	 * Client's occupation
	 */
	client_occupation?: string | null;
	/**
	 * Purpose of journey
	 */
	purpose_of_journey?: string | null;
	/**
	 * Pre-existing damage
	 */
	pre_existing_damage?: string | null;
	/**
	 * Description of client's damage
	 */
	client_damage_description?: string | null;
	/**
	 * Number of passengers Clients
	 */
	no_of_passengers_clients?: number | null;
	/**
	 * Client's speed
	 */
	client_speed?: string | null;
	/**
	 * Were there any witnesses
	 */
	any_witnesses?: boolean | null;
	/**
	 * Weather conditions
	 */
	weather_conditions?: string | null;
	/**
	 * Source of liability
	 */
	source_liability?: string | null;
	/**
	 * Emergency service attended
	 */
	emergency_service_attend?: boolean | null;
	/**
	 * Did police attend
	 */
	police_attended?: boolean | null;
	/**
	 * Did fire brigade attend
	 */
	fire_brigade_attended?: boolean | null;
	/**
	 * Did ambulance attend
	 */
	ambulance_attended?: boolean | null;
	/**
	 * Name of Police station
	 */
	police_station_name?: string | null;
	/**
	 * Name of Police officer
	 */
	police_officer_name?: string | null;
	/**
	 * Police Reference
	 */
	police_reference?: string | null;
	/**
	 * Policy Cover
	 */
	policy_cover?: string | null;
	/**
	 * Policy Excess
	 */
	policy_excess?: number | null;
	/**
	 * Policy Start Date
	 */
	policy_start_date?: string | null;
	/**
	 * Policy Expires Date
	 */
	policy_expiry_date?: string | null;
	/**
	 * VAT Registered
	 */
	vat_registered?: boolean | null;
	/**
	 * Policy vehicle use
	 */
	policy_vehicle_use?: string | null;
	/**
	 * Client Vehicle Drivable
	 */
	client_vehicle_drivable?: boolean | null;
	/**
	 * Client Vehicle Secure
	 */
	client_vehicle_secure?: boolean | null;
	/**
	 * Client Vehicle Location
	 */
	client_vehicle_location?: string | null;
	storage?: boolean | null;
	storage_location?: string | null;
};



export type ClaimPublic = {
	client_firstname: string;
	client_lastname: string;
	client_address_1?: string | null;
	client_address_town?: string | null;
	client_address_postcode?: string | null;
	client_vehicle_reg?: string | null;
	client_insurer?: string | null;
	client_insurer_id?: number | null;
	client_insurer_policyno?: string | null;
	client_phone?: string | null;
	client_mobile?: string | null;
	client_email?: string | null;
	authority_id?: number | null;
	plate_type?: string | null;
	accident_date?: string | null;
	accident_time?: string | null;
	accident_location?: string | null;
	accident_circs?: string | null;
	tp_firstname?: string | null;
	tp_lastname?: string | null;
	tp_address_1?: string | null;
	tp_address_town?: string | null;
	tp_address_postcode?: string | null;
	tp_vehicle_reg?: string | null;
	tp_insurer?: string | null;
	tp_insurer_id?: number | null;
	tp_insurer_polcyno?: string | null;
	tp_phone?: string | null;
	tp_mobile?: string | null;
	referral_id?: number | null;
	circs_grade_id?: number | null;
	/**
	 * Client's date of birth
	 */
	client_dob?: string | null;
	/**
	 * Client's occupation
	 */
	client_occupation?: string | null;
	/**
	 * Purpose of journey
	 */
	purpose_of_journey?: string | null;
	/**
	 * Pre-existing damage
	 */
	pre_existing_damage?: string | null;
	/**
	 * Description of client's damage
	 */
	client_damage_description?: string | null;
	/**
	 * Number of passengers Clients
	 */
	no_of_passengers_clients?: number | null;
	/**
	 * Client's speed
	 */
	client_speed?: string | null;
	/**
	 * Were there any witnesses
	 */
	any_witnesses?: boolean | null;
	/**
	 * Weather conditions
	 */
	weather_conditions?: string | null;
	/**
	 * Source of liability
	 */
	source_liability?: string | null;
	/**
	 * Emergency service attended
	 */
	emergency_service_attend?: boolean | null;
	/**
	 * Did police attend
	 */
	police_attended?: boolean | null;
	/**
	 * Did fire brigade attend
	 */
	fire_brigade_attended?: boolean | null;
	/**
	 * Did ambulance attend
	 */
	ambulance_attended?: boolean | null;
	/**
	 * Name of Police station
	 */
	police_station_name?: string | null;
	/**
	 * Name of Police officer
	 */
	police_officer_name?: string | null;
	/**
	 * Police Reference
	 */
	police_reference?: string | null;
	/**
	 * Policy Cover
	 */
	policy_cover?: string | null;
	/**
	 * Policy Excess
	 */
	policy_excess?: number | null;
	/**
	 * Policy Start Date
	 */
	policy_start_date?: string | null;
	/**
	 * Policy Expires Date
	 */
	policy_expiry_date?: string | null;
	/**
	 * VAT Registered
	 */
	vat_registered?: boolean | null;
	/**
	 * Policy vehicle use
	 */
	policy_vehicle_use?: string | null;
	/**
	 * Client Vehicle Drivable
	 */
	client_vehicle_drivable?: boolean | null;
	/**
	 * Client Vehicle Secure
	 */
	client_vehicle_secure?: boolean | null;
	/**
	 * Client Vehicle Location
	 */
	client_vehicle_location?: string | null;
	storage?: boolean | null;
	storage_location?: string | null;
	id: number;
	created_datetime: string;
	claimscore?: Claim_Score | null;
	annieresult?: Annie_Result | null;
};



export type ClaimSummaryDetailPublic = {
	id: number;
	referral_id: number;
	user_id: number | null;
	task_id: string | null;
	tenant_id: string | null;
	workflow_id: number | null;
	version: string;
	priority: number | null;
	summary: string | null;
	status: string;
	error_message: string | null;
	generation_time: number | null;
	cache_hit: boolean;
	model_version: string | null;
	created_datetime: string;
	updated_datetime: string;
	system_prompt: string | null;
	user_prompt: string | null;
	context_data: Record<string, unknown> | null;
};



export type ClaimSummaryPublic = {
	id: number;
	referral_id: number;
	user_id: number | null;
	task_id: string | null;
	tenant_id: string | null;
	workflow_id: number | null;
	version: string;
	priority: number | null;
	summary: string | null;
	status: string;
	error_message: string | null;
	generation_time: number | null;
	cache_hit: boolean;
	model_version: string | null;
	created_datetime: string;
	updated_datetime: string;
};



export type ClaimSummaryRecentRunPublic = {
	id: number;
	referralId: number;
	sourceRef?: string | null;
	hireStatus?: string | null;
	taskId?: string | null;
	userId?: number | null;
	tenantId?: string | null;
	workflowId?: number | null;
	version: string;
	status: string;
	summaryExcerpt?: string | null;
	errorMessage?: string | null;
	generationTime?: number | null;
	cacheHit: boolean;
	modelVersion?: string | null;
	createdDatetime: string;
	updatedDatetime: string;
	feedbackRating?: string | null;
};



export type ClaimSummaryRecentRunsPublic = {
	data: Array<ClaimSummaryRecentRunPublic>;
	count: number;
};



export type ClaimUpdate = {
	client_firstname?: string | null;
	client_lastname?: string | null;
	client_address_1?: string | null;
	client_address_town?: string | null;
	client_address_postcode?: string | null;
	client_vehicle_reg?: string | null;
	client_insurer?: string | null;
	client_insurer_id?: number | null;
	client_insurer_policyno?: string | null;
	client_phone?: string | null;
	client_mobile?: string | null;
	client_email?: string | null;
	authority_id?: number | null;
	plate_type?: string | null;
	accident_date?: string | null;
	accident_time?: string | null;
	accident_location?: string | null;
	accident_circs?: string | null;
	tp_firstname?: string | null;
	tp_lastname?: string | null;
	tp_address_1?: string | null;
	tp_address_town?: string | null;
	tp_address_postcode?: string | null;
	tp_vehicle_reg?: string | null;
	tp_insurer?: string | null;
	tp_insurer_id?: number | null;
	tp_insurer_polcyno?: string | null;
	tp_phone?: string | null;
	tp_mobile?: string | null;
	referral_id?: number | null;
	circs_grade_id?: number | null;
	client_dob?: string | null;
	client_occupation?: string | null;
	purpose_of_journey?: string | null;
	pre_existing_damage?: string | null;
	client_damage_description?: string | null;
	no_of_passengers_clients?: number | null;
	client_speed?: string | null;
	any_witnesses?: boolean | null;
	weather_conditions?: string | null;
	source_liability?: string | null;
	emergency_service_attend?: boolean | null;
	police_attended?: boolean | null;
	fire_brigade_attended?: boolean | null;
	ambulance_attended?: boolean | null;
	police_station_name?: string | null;
	police_officer_name?: string | null;
	police_reference?: string | null;
	policy_cover?: string | null;
	policy_excess?: number | null;
	policy_start_date?: string | null;
	policy_expiry_date?: string | null;
	vat_registered?: boolean | null;
	policy_vehicle_use?: string | null;
	client_vehicle_drivable?: boolean | null;
	client_vehicle_secure?: boolean | null;
	client_vehicle_location?: string | null;
	storage?: boolean | null;
	storage_location?: string | null;
	claimscore?: number | null;
	annieresult?: number | null;
};



export type Claim_Score = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	claim_id?: number | null;
	completeness_CL_score?: number | null;
	completeness_TP_score?: number | null;
	tpreg_score?: number | null;
	timeliness_score?: number | null;
	total_score?: number | null;
	final_score?: number | null;
	id?: number | null;
};



export type ClaimsPublic = {
	data: Array<ClaimPublic>;
	count: number;
};



export type CloneRequest = {
	target_partner_id?: number | null;
};



export type CompareRequest = {
	referral_id: number;
	workflow_a: WorkflowSpec;
	workflow_b: WorkflowSpec;
};



export type ComplaintCreate = {
	referralId: number;
	partnerId?: number | null;
	summary: string;
	description?: string | null;
	status?: ComplaintStatus;
	notifySource?: boolean;
	notifySuppliers?: Array<number>;
	notifyWysehub?: boolean;
};



export type ComplaintPublic = {
	id: number;
	referralId: number;
	partnerId: number;
	summary: string;
	description: string | null;
	status: ComplaintStatus;
	dateOpened: string;
	dateClosed: string | null;
	resolutionNotes: string | null;
	createdDatetime: string;
	updatedDatetime: string;
	partner?: PartnerPublic | null;
};



/**
 * Status enum for complaints
 */
export type ComplaintStatus = 'Open' | 'InProgress' | 'Resolved' | 'Closed';



export type ComplaintUpdate = {
	summary?: string | null;
	description?: string | null;
	status?: ComplaintStatus | null;
	resolutionNotes?: string | null;
	dateClosed?: string | null;
};



export type ComplaintsPublic = {
	data: Array<ComplaintPublic>;
	count: number;
};



export type EditRequest = {
	definition: Record<string, unknown>;
	display_name?: string | null;
	status?: string | null;
};



export type FeedbackRequestPublic = {
	agentRunId: string;
	referralId?: number | null;
	agentKey: string;
	recipientEmail: string;
	agentaTraceId?: string | null;
	agentaSpanId?: string | null;
	promptSource?: string | null;
	promptAppSlug?: string | null;
	promptVariantSlug?: string | null;
	promptEnvironment?: string | null;
	promptVersionNumber?: number | null;
	/**
	 * pending, responded, expired
	 */
	status?: string;
	expiresAt?: string | null;
	respondedAt?: string | null;
	id: string;
	token: string;
	created_at: string;
};



export type FeedbackResponsePublic = {
	feedbackRequestId: string;
	/**
	 * up or down
	 */
	rating: string;
	reasonCode?: string | null;
	comment?: string | null;
	submittedByEmail?: string | null;
	submittedAt?: string;
	/**
	 * pending, synced, error, skipped
	 */
	agentaSyncStatus?: string;
	agentaAnnotationId?: string | null;
	agentaSyncedAt?: string | null;
	agentaSyncError?: string | null;
	id: string;
};



export type HTTPValidationError = {
	detail?: Array<ValidationError>;
};



export type Input = {
	text: string;
};



export type InsurerCreate = {
	name: string;
};



export type InsurerPublic = {
	name: string;
	id: number;
};



export type InsurerUpdate = {
	name: string;
};



export type InsurersPublic = {
	data: Array<InsurerPublic>;
	count: number;
};



export type Interested_Person = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	ip_type?: string | null;
	firstname?: string | null;
	lastname?: string | null;
	address_1?: string | null;
	address_town?: string | null;
	address_postcode?: string | null;
	vehicle_reg?: string | null;
	vehicle_make?: string | null;
	vehicle_model?: string | null;
	insurer?: string | null;
	insurer_policyno?: string | null;
	phone?: string | null;
	mobile?: string | null;
	email?: string | null;
	id?: number | null;
};



export type Interested_PersonBase = {
	referral_id?: number | null;
	ip_type?: string | null;
	firstname?: string | null;
	lastname?: string | null;
	address_1?: string | null;
	address_town?: string | null;
	address_postcode?: string | null;
	vehicle_reg?: string | null;
	vehicle_make?: string | null;
	vehicle_model?: string | null;
	insurer?: string | null;
	insurer_policyno?: string | null;
	phone?: string | null;
	mobile?: string | null;
	email?: string | null;
};



export type InvoiceLinePublic = {
	invoice_id: number | null;
	referral_id: number | null;
	referral_allocation_id: number | null;
	description: string;
	rate?: number;
	amount: number;
	billed_amount?: number | null;
	discount_percentage?: number | null;
	status?: string | null;
	type?: string | null;
	paid_date?: string | null;
	id: number;
};



export type InvoicePublic = {
	partner_id: number;
	billing_date: string;
	total_amount: number;
	issued_date?: string;
	status?: string;
	/**
	 * Location of the invoice file
	 */
	file_location?: string | null;
	type?: string | null;
	xero_inv_no?: string | null;
	/**
	 * Xero internal invoice ID/GUID for API requests
	 */
	xero_invoice_id?: string | null;
	id: number;
	invoice_lines: Array<InvoiceLinePublic>;
};



export type LoginResponse = {
	accessToken?: string | null;
	tokenType: string;
	requiresOtp: boolean;
	message: string;
	loginHistoryId?: number | null;
	userId?: number | null;
	csrfToken?: string | null;
};



export type Message = {
	message: string;
};



/**
 * Model for creating Metabase report schedules.
 */
export type MetabaseReportScheduleCreate = {
	/**
	 * Friendly name for the report schedule
	 */
	name: string;
	/**
	 * Metabase card ID to export
	 */
	card_id: number;
	/**
	 * Comma/semicolon separated recipient list
	 */
	email_to: string;
	/**
	 * Email subject override
	 */
	subject?: string | null;
	/**
	 * UTC hour to send (0-23)
	 */
	run_hour: number;
	/**
	 * UTC minute to send (0-59)
	 */
	run_minute: number;
	/**
	 * Whether the schedule is active
	 */
	is_enabled?: boolean;
};



/**
 * Public model for Metabase report schedules.
 */
export type MetabaseReportSchedulePublic = {
	/**
	 * Friendly name for the report schedule
	 */
	name: string;
	/**
	 * Metabase card ID to export
	 */
	card_id: number;
	/**
	 * Comma/semicolon separated recipient list
	 */
	email_to: string;
	/**
	 * Email subject override
	 */
	subject?: string | null;
	/**
	 * UTC hour to send (0-23)
	 */
	run_hour: number;
	/**
	 * UTC minute to send (0-59)
	 */
	run_minute: number;
	/**
	 * Whether the schedule is active
	 */
	is_enabled?: boolean;
	id: number;
	last_sent_at?: string | null;
	created_datetime: string;
	updated_datetime?: string | null;
};



/**
 * Model for updating Metabase report schedules.
 */
export type MetabaseReportScheduleUpdate = {
	name?: string | null;
	card_id?: number | null;
	email_to?: string | null;
	subject?: string | null;
	run_hour?: number | null;
	run_minute?: number | null;
	is_enabled?: boolean | null;
	last_sent_at?: string | null;
};



/**
 * Public model for list of Metabase report schedules.
 */
export type MetabaseReportSchedulesPublic = {
	data: Array<MetabaseReportSchedulePublic>;
	count: number;
};



export type MyPagesResponse = {
	pages: Array<string>;
};



export type NewPassword = {
	token: string;
	new_password: string;
};



export type Note = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	referralId?: number | null;
	complaintId?: number | null;
	note: string;
	createdBy: number;
	noteDate?: string;
	id?: number | null;
};



export type NoteCreate = {
	referralId?: number | null;
	complaintId?: number | null;
	note: string;
	noteDate?: string;
	notifySource?: boolean;
	notifySuppliers?: Array<number>;
	notifyWysehub?: boolean;
};



export type NotePublic = {
	id: number;
	referral_id: number | null;
	complaint_id: number | null;
	note: string;
	created_datetime: string;
	created_by_user?: UserPublicBasic | null;
	note_date?: string | null;
};



export type NotePublicBasic = {
	id: number;
};



export type NotesBatchCreate = {
	notes: Array<NoteCreate>;
};



export type NotesBatchError = {
	index: number;
	referralId?: number | null;
	detail: string;
};



export type NotesBatchResult = {
	data: Array<NotePublicBasic>;
	count: number;
	errors?: Array<NotesBatchError>;
};



export type NotesPublic = {
	data: Array<NotePublic>;
	count: number;
};



export type Partner = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	name: string;
	address1?: string | null;
	addressTown?: string | null;
	addressPostcode?: string | null;
	supplierBaseRate?: number | null;
	email?: string | null;
	Tel?: number | null;
	type?: string | null;
	updatesEmail?: string | null;
	accountsEmail?: string | null;
	newclaimsEmail?: string | null;
	receiveClaimsVia?: string | null;
	tier?: string | null;
	xeroContactNumber?: string | null;
	isActive?: boolean;
	id?: number | null;
};



export type PartnerApiBillingConfigCreate = {
	partner_id: number;
	billing_type: string;
	is_active?: boolean;
	pricing_tiers?: Array<Record<string, unknown>> | null;
	discount_type?: string | null;
	discount_duration_months?: number | null;
	discount_percentage?: number | null;
	discount_start_date?: string | null;
	prepaid_balance?: number | null;
	prepaid_requests_remaining?: number | null;
	billing_day?: number | null;
	max_monthly_charge?: number | null;
	minimum_combined_invoice_total?: number | null;
};



export type PartnerApiBillingConfigPublic = {
	partner_id: number;
	billing_type: string;
	is_active?: boolean;
	pricing_tiers?: Array<Record<string, unknown>> | null;
	discount_type?: string | null;
	discount_duration_months?: number | null;
	discount_percentage?: number | null;
	discount_start_date?: string | null;
	prepaid_balance?: number | null;
	prepaid_requests_remaining?: number | null;
	billing_day?: number | null;
	max_monthly_charge?: number | null;
	minimum_combined_invoice_total?: number | null;
	id: number;
};



export type PartnerApiBillingConfigUpdate = {
	partner_id: number;
	billing_type?: string | null;
	is_active?: boolean | null;
	pricing_tiers?: Array<Record<string, unknown>> | null;
	discount_type?: string | null;
	discount_duration_months?: number | null;
	discount_percentage?: number | null;
	discount_start_date?: string | null;
	prepaid_balance?: number | null;
	prepaid_requests_remaining?: number | null;
	billing_day?: number | null;
	max_monthly_charge?: number | null;
	minimum_combined_invoice_total?: number | null;
};



export type PartnerApiBillingConfigsPublic = {
	data: Array<PartnerApiBillingConfigPublic>;
	count: number;
};



export type PartnerCreate = {
	name: string;
	address1?: string | null;
	addressTown?: string | null;
	addressPostcode?: string | null;
	supplierBaseRate?: number | null;
	email?: string | null;
	Tel?: number | null;
	type?: string | null;
	updatesEmail?: string | null;
	accountsEmail?: string | null;
	newclaimsEmail?: string | null;
	receiveClaimsVia?: string | null;
	tier?: string | null;
	xeroContactNumber?: string | null;
	isActive?: boolean;
};



export type PartnerForNotification = {
	id: number;
	name: string;
	email?: string | null;
	updates_email?: string | null;
	type?: string | null;
};



export type PartnerPublic = {
	name: string;
	address1?: string | null;
	addressTown?: string | null;
	addressPostcode?: string | null;
	supplierBaseRate?: number | null;
	email?: string | null;
	Tel?: number | null;
	type?: string | null;
	updatesEmail?: string | null;
	accountsEmail?: string | null;
	newclaimsEmail?: string | null;
	receiveClaimsVia?: string | null;
	tier?: string | null;
	xeroContactNumber?: string | null;
	isActive?: boolean;
	id: number;
};



export type PartnerUpdate = {
	name?: string | null;
	address1?: string | null;
	addressTown?: string | null;
	addressPostcode?: string | null;
	supplierBaseRate?: number | null;
	email?: string | null;
	Tel?: number | null;
	type?: string | null;
	updatesEmail?: string | null;
	accountsEmail?: string | null;
	newclaimsEmail?: string | null;
	receiveClaimsVia?: string | null;
	tier?: string | null;
	xeroContactNumber?: string | null;
	isActive?: boolean;
};



export type Partner_RuleCreate = {
	partner_id?: number | null;
	circs_grade_id?: number | null;
	veh_group_id?: number | null;
	rule_name?: string | null;
	rule?: string | null;
	vehicle_type?: string | null;
	probability_threshold?: string | null;
	outbound_email?: string | null;
	outbound_phone?: string | null;
	priority?: number | null;
	partner_percents?: Array<unknown> | null;
};



export type Partner_RulePublic = {
	partner_id?: number | null;
	circs_grade_id?: number | null;
	veh_group_id?: number | null;
	rule_name?: string | null;
	rule?: string | null;
	vehicle_type?: string | null;
	probability_threshold?: string | null;
	outbound_email?: string | null;
	outbound_phone?: string | null;
	priority?: number | null;
	id: number;
	partner: Partner;
	circs_grade: Circs_Grade | null;
};



export type Partner_RuleUpdate = {
	partner_id?: number | null;
	circs_grade_id?: number | null;
	veh_group_id?: number | null;
	rule_name?: string | null;
	rule?: string | null;
	vehicle_type?: string | null;
	probability_threshold?: string | null;
	outbound_email?: string | null;
	outbound_phone?: string | null;
	priority?: number | null;
};



export type Partner_RulesPublic = {
	data: Array<Partner_RulePublic>;
	count: number;
};



export type PartnersPublic = {
	data: Array<PartnerPublic>;
	count: number;
};



/**
 * Performance metric data from frontend.
 */
export type PerformanceMetric = {
	/**
	 * Metric name (LCP, INP, CLS, etc.)
	 */
	metric: string;
	/**
	 * Metric value
	 */
	value: number;
	/**
	 * Performance rating (good, needs-improvement, poor)
	 */
	rating: string;
	/**
	 * Timestamp when metric was recorded
	 */
	timestamp: number;
	/**
	 * URL where metric was recorded
	 */
	url: string;
	/**
	 * User agent string
	 */
	userAgent: string;
};



export type PermissionCatalogItem = {
	key: string;
	operations: Array<string>;
};



export type PermissionCatalogResponse = {
	endpoints: Array<PermissionCatalogItem>;
	pages: Array<PermissionCatalogItem>;
};



export type PrivacyPurgeResponse = {
	deleted: number;
};



export type PrivacyRehydrateRequest = {
	scopeKey: string;
	token: string;
	justification: string;
};



export type PrivacyRehydrateResponse = {
	found: boolean;
	resolvedValue?: string | null;
};



export type ProfilerStats = {
	active: boolean;
	tracked_patterns: number;
	slow_queries: number;
	n1_patterns: number;
};



/**
 * Model for creating new rate limiting configurations.
 */
export type RateLimitConfigCreate = {
	/**
	 * Endpoint path pattern (e.g., /api/v1/login/access-token)
	 */
	endpoint_path: string;
	/**
	 * HTTP method (GET, POST, PUT, DELETE, etc.)
	 */
	method: string;
	/**
	 * Rate limiting strategy (redis, database, hybrid)
	 */
	strategy?: string;
	/**
	 * Identifier type (user, ip, api_key, path_user)
	 */
	identifier_type?: string;
	/**
	 * Resource name for rate limiting
	 */
	resource_name: string;
	/**
	 * Default requests per window
	 */
	default_requests?: number;
	/**
	 * Default window in seconds
	 */
	default_window_seconds?: number;
	/**
	 * Default burst allowance
	 */
	default_burst_allowance?: number;
	/**
	 * Admin requests per window
	 */
	admin_requests?: number | null;
	/**
	 * Admin window in seconds
	 */
	admin_window_seconds?: number | null;
	/**
	 * Admin burst allowance
	 */
	admin_burst_allowance?: number | null;
	/**
	 * Role-specific rate limit overrides
	 */
	role_overrides?: Record<string, unknown> | null;
	/**
	 * Whether rate limiting is enabled for this endpoint
	 */
	enabled?: boolean;
	/**
	 * Description of the endpoint and its rate limits
	 */
	description?: string | null;
};



/**
 * Public model for rate limiting configurations.
 */
export type RateLimitConfigPublic = {
	/**
	 * Endpoint path pattern (e.g., /api/v1/login/access-token)
	 */
	endpoint_path: string;
	/**
	 * HTTP method (GET, POST, PUT, DELETE, etc.)
	 */
	method: string;
	/**
	 * Rate limiting strategy (redis, database, hybrid)
	 */
	strategy?: string;
	/**
	 * Identifier type (user, ip, api_key, path_user)
	 */
	identifier_type?: string;
	/**
	 * Resource name for rate limiting
	 */
	resource_name: string;
	/**
	 * Default requests per window
	 */
	default_requests?: number;
	/**
	 * Default window in seconds
	 */
	default_window_seconds?: number;
	/**
	 * Default burst allowance
	 */
	default_burst_allowance?: number;
	/**
	 * Admin requests per window
	 */
	admin_requests?: number | null;
	/**
	 * Admin window in seconds
	 */
	admin_window_seconds?: number | null;
	/**
	 * Admin burst allowance
	 */
	admin_burst_allowance?: number | null;
	/**
	 * Role-specific rate limit overrides
	 */
	role_overrides?: Record<string, unknown> | null;
	/**
	 * Whether rate limiting is enabled for this endpoint
	 */
	enabled?: boolean;
	/**
	 * Description of the endpoint and its rate limits
	 */
	description?: string | null;
	id: number;
	created_datetime: string;
	updated_datetime: string;
};



/**
 * Model for updating rate limiting configurations.
 */
export type RateLimitConfigUpdate = {
	strategy?: string | null;
	identifier_type?: string | null;
	resource_name?: string | null;
	default_requests?: number | null;
	default_window_seconds?: number | null;
	default_burst_allowance?: number | null;
	admin_requests?: number | null;
	admin_window_seconds?: number | null;
	admin_burst_allowance?: number | null;
	role_overrides?: Record<string, unknown> | null;
	enabled?: boolean | null;
	description?: string | null;
};



/**
 * Public model for list of rate limiting configurations.
 */
export type RateLimitConfigsPublic = {
	data: Array<RateLimitConfigPublic>;
	count: number;
};



export type ReferralCreate = {
	partnerSourceId?: number | null;
	partnerSupplierId?: number | null;
	type?: string | null;
	authorityId?: number | null;
	claimId?: number | null;
	status?: string | null;
	sourceOrigNote?: string | null;
	sourceRef?: string | null;
};



export type ReferralNotificationOptions = {
	source_partner?: PartnerForNotification | null;
	supplier_partners: Array<PartnerForNotification>;
	source_ref?: string | null;
};



export type ReferralPublic = {
	partnerSourceId?: number | null;
	partnerSupplierId?: number | null;
	type?: string | null;
	authorityId?: number | null;
	claimId?: number | null;
	status?: string | null;
	sourceOrigNote?: string | null;
	sourceRef?: string | null;
	id: number;
	partner_source: Partner | null;
	partner_supplier: Partner | null;
	claim: Claim | null;
	hire_details?: Record<string, (string | number | null)> | null;
	createdDatetime: string;
	updatedDatetime: string;
	uploads?: Array<Upload> | null;
	notes?: Array<Note> | null;
	totalScore?: number | null;
	adjustedLiabilityScore?: number | null;
	interestedPersons: Array<Interested_Person>;
	referralAllocations?: Array<Referral_AllocationPublic> | null;
};



export type ReferralUpdate = {
	partnerSourceId?: number | null;
	partnerSupplierId?: number | null;
	type?: string | null;
	authorityId?: number | null;
	claimId?: number | null;
	status?: string | null;
	sourceOrigNote?: string | null;
	sourceRef?: string | null;
	daysToHire?: number | null;
	creditRepair?: string | null;
};



export type Referral_AllocationCreate = {
	partner_id?: number | null;
	referral_id?: number | null;
	sentdate?: string | null;
	hirestatus?: string | null;
	responsedate?: string | null;
	hirestartdate?: string | null;
	hireenddate?: string | null;
	hire_veh_group_id?: number | null;
	allocation_type?: string | null;
	pending_reason?: string | null;
	rejection_reason?: string | null;
	supplier_ref?: string | null;
	first_contact_date?: string | null;
	FNOL_completed_date?: string | null;
	hire_requested_date?: string | null;
	repair?: boolean | null;
	hire?: boolean | null;
	second_tier?: boolean | null;
	repair_status?: string | null;
	repair_cancellation_reason?: string | null;
	repair_bid?: string | null;
	repair_start_date?: string | null;
	repair_end_date?: string | null;
	repair_invoice_date?: string | null;
	cancelled_date?: string | null;
};



export type Referral_AllocationPublic = {
	id: number;
	partner_id: number;
	sentdate?: string | null;
	responsedate?: string | null;
	allocation_type?: string | null;
	hirestatus?: string | null;
	hirestartdate?: string | null;
	hireenddate?: string | null;
	pending_reason?: string | null;
	rejection_reason?: string | null;
	supplier_ref?: string | null;
	first_contact_date?: string | null;
	FNOL_completed_date?: string | null;
	hire_requested_date?: string | null;
	partner?: Partner | null;
	repair?: boolean | null;
	hire?: boolean | null;
	second_tier?: boolean | null;
	repair_status?: string | null;
	repair_cancellation_reason?: string | null;
	repair_bid?: string | null;
	repair_start_date?: string | null;
	repair_end_date?: string | null;
	repair_invoice_date?: string | null;
};



export type Referral_AllocationReallocate = {
	/**
	 * ID of the referral to reallocate
	 */
	referral_id: number;
	/**
	 * ID of the new partner to allocate to
	 */
	new_partner_id: number;
	/**
	 * Type of allocation
	 */
	allocation_type?: string | null;
	/**
	 * Initial hire status
	 */
	hirestatus?: string | null;
	/**
	 * Hire start date
	 */
	hirestartdate?: string | null;
	/**
	 * Hire end date
	 */
	hireenddate?: string | null;
	/**
	 * Optional note explaining why reallocation is needed
	 */
	reallocation_note?: string | null;
	/**
	 * Whether to send notifications and trigger external API calls
	 */
	send_notifications?: boolean;
};



export type Referral_AllocationUpdate = {
	partner_id?: number | null;
	referral_id: number;
	hirestatus?: string | null;
	hirestartdate?: string | null;
	hireenddate?: string | null;
	hire_veh_group_id?: number | null;
	pending_reason?: string | null;
	rejection_reason?: string | null;
	supplier_ref?: string | null;
	first_contact_date?: string | null;
	FNOL_completed_date?: string | null;
	hire_requested_date?: string | null;
	repair?: boolean | null;
	hire?: boolean | null;
	second_tier?: boolean | null;
	repair_status?: string | null;
	repair_cancellation_reason?: string | null;
	repair_bid?: string | null;
	repair_start_date?: string | null;
	repair_end_date?: string | null;
	repair_invoice_date?: string | null;
	cancelled_date?: string | null;
	client_vehicle_drivable?: boolean | string | null;
};



export type Referral_AllocationsPublic = {
	data: Array<Referral_AllocationPublic>;
	count: number;
};



export type ReferralsPublic = {
	data: Array<ReferralPublic>;
	count: number;
};



export type ReplaySyncResult = {
	enqueued?: Array<string>;
	skipped?: string | null;
};



/**
 * Model for creating a report template.
 */
export type ReportTemplateCreate = {
	/**
	 * Display name for the template (e.g. "Standard Matt Audit")
	 */
	name: string;
	/**
	 * What this template is used for
	 */
	description?: string | null;
	/**
	 * Jinja2 subject line template, e.g. "Audit Report - {{ system.report_date }}"
	 */
	subjectTemplate?: string | null;
	/**
	 * Jinja2 HTML for the per-urgent-claim card (batch) or full body (legacy)
	 */
	emailHtml?: string | null;
	/**
	 * Jinja2 HTML intro (batch context); empty uses default Hi + metrics table
	 */
	emailIntroHtml?: string | null;
	/**
	 * Jinja2 HTML footer (batch context); empty uses default sign-off
	 */
	emailFooterHtml?: string | null;
	/**
	 * Full Jinja2 HTML template rendered to PDF via WeasyPrint
	 */
	pdfHtml?: string | null;
	/**
	 * Ordered list of {path, label} objects for CSV column definitions
	 */
	csvFields?: Array<unknown> | null;
	/**
	 * Ordered list of {path, label} objects for Excel column definitions
	 */
	xlsxFields?: Array<unknown> | null;
	/**
	 * Dict of enabled output channels e.g. {"email": true, "csv": false, "pdf": true, "xlsx": false}
	 */
	channelsEnabled?: Record<string, unknown> | null;
	/**
	 * Informational: which bot_name values this template is designed for
	 */
	mergeBotNames?: Array<unknown> | null;
	/**
	 * Whether this template is available for use
	 */
	isActive?: boolean;
};



/**
 * Public model for a report template.
 */
export type ReportTemplatePublic = {
	/**
	 * Display name for the template (e.g. "Standard Matt Audit")
	 */
	name: string;
	/**
	 * What this template is used for
	 */
	description?: string | null;
	/**
	 * Jinja2 subject line template, e.g. "Audit Report - {{ system.report_date }}"
	 */
	subjectTemplate?: string | null;
	/**
	 * Jinja2 HTML for the per-urgent-claim card (batch) or full body (legacy)
	 */
	emailHtml?: string | null;
	/**
	 * Jinja2 HTML intro (batch context); empty uses default Hi + metrics table
	 */
	emailIntroHtml?: string | null;
	/**
	 * Jinja2 HTML footer (batch context); empty uses default sign-off
	 */
	emailFooterHtml?: string | null;
	/**
	 * Full Jinja2 HTML template rendered to PDF via WeasyPrint
	 */
	pdfHtml?: string | null;
	/**
	 * Ordered list of {path, label} objects for CSV column definitions
	 */
	csvFields?: Array<unknown> | null;
	/**
	 * Ordered list of {path, label} objects for Excel column definitions
	 */
	xlsxFields?: Array<unknown> | null;
	/**
	 * Dict of enabled output channels e.g. {"email": true, "csv": false, "pdf": true, "xlsx": false}
	 */
	channelsEnabled?: Record<string, unknown> | null;
	/**
	 * Informational: which bot_name values this template is designed for
	 */
	mergeBotNames?: Array<unknown> | null;
	/**
	 * Whether this template is available for use
	 */
	isActive?: boolean;
	id: number;
	createdDatetime: string;
	updatedDatetime?: string | null;
};



/**
 * Model for updating a report template (all fields optional).
 */
export type ReportTemplateUpdate = {
	name?: string | null;
	description?: string | null;
	subjectTemplate?: string | null;
	emailHtml?: string | null;
	emailIntroHtml?: string | null;
	emailFooterHtml?: string | null;
	pdfHtml?: string | null;
	csvFields?: Array<unknown> | null;
	xlsxFields?: Array<unknown> | null;
	channelsEnabled?: Record<string, unknown> | null;
	mergeBotNames?: Array<unknown> | null;
	isActive?: boolean | null;
};



/**
 * Public model for a list of report templates.
 */
export type ReportTemplatesPublic = {
	data: Array<ReportTemplatePublic>;
	count: number;
};



export type RequestCreate = {
	/**
	 * ID of the source partner
	 */
	partner_source_id: number;
	/**
	 * Type of the request
	 */
	type?: string | null;
	/**
	 * Number of days the vehicle is hired for
	 */
	daystohire?: number | null;
	/**
	 * Credit repair status
	 */
	creditrepair?: string | null;
	/**
	 * ID of the authority
	 */
	authority_id?: number | null;
	/**
	 * Client's first name
	 */
	client_firstname: string;
	/**
	 * Client's last name
	 */
	client_lastname: string;
	/**
	 * First line of client's address
	 */
	client_address_1?: string | null;
	/**
	 * Client's town
	 */
	client_address_town?: string | null;
	/**
	 * Client's postcode
	 */
	client_address_postcode?: string | null;
	/**
	 * Client's vehicle registration
	 */
	client_vehicle_reg?: string | null;
	/**
	 * Client's insurance company
	 */
	client_insurer?: string | null;
	/**
	 * Client's insurance policy number
	 */
	client_insurer_policyno?: string | null;
	/**
	 * Client's phone number
	 */
	client_phone?: string | null;
	/**
	 * Client's mobile number
	 */
	client_mobile?: string | null;
	/**
	 * Client's email address
	 */
	client_email?: string | null;
	/**
	 * License plate type
	 */
	plate_type?: string | null;
	/**
	 * Driver First name
	 */
	driver_firstname?: string | null;
	/**
	 * Driver Last name
	 */
	driver_lastname?: string | null;
	/**
	 * Driver Address
	 */
	driver_address?: string | null;
	/**
	 * Driver Postcode
	 */
	driver_postcode?: string | null;
	/**
	 * Driver Phone
	 */
	driver_phone?: string | null;
	/**
	 * Driver Mobile
	 */
	driver_mobile?: string | null;
	/**
	 * Driver Email
	 */
	driver_email?: string | null;
	/**
	 * Driver Date of Birth
	 */
	driver_dob?: string | null;
	/**
	 * Driver Occupation
	 */
	driver_occupation?: string | null;
	/**
	 * Date of the accident
	 */
	accident_date?: string | null;
	/**
	 * Time of the accident
	 */
	accident_time?: string | null;
	/**
	 * Location where the accident occurred
	 */
	accident_location?: string | null;
	/**
	 * Circumstances of the accident
	 */
	accident_circs?: string | null;
	/**
	 * Third party's first name
	 */
	tp_firstname?: string | null;
	/**
	 * Third party's last name
	 */
	tp_lastname?: string | null;
	/**
	 * First line of third party's address
	 */
	tp_address_1?: string | null;
	/**
	 * Third party's town
	 */
	tp_address_town?: string | null;
	/**
	 * Third party's postcode
	 */
	tp_address_postcode?: string | null;
	/**
	 * Third party's vehicle registration
	 */
	tp_vehicle_reg?: string | null;
	/**
	 * Third party's insurance company
	 */
	tp_insurer?: string | null;
	/**
	 * Third party's insurance policy number
	 */
	tp_insurer_polcyno?: string | null;
	/**
	 * Third party's phone number
	 */
	tp_phone?: string | null;
	/**
	 * Third party's mobile number
	 */
	tp_mobile?: string | null;
	/**
	 * ID of the circumstances grade
	 */
	circs_grade_id?: number | null;
	/**
	 * ID of the vehicle group
	 */
	veh_group_id?: number | null;
	/**
	 * Date and time of creation
	 */
	created_datetime?: string | null;
	/**
	 * Type of allocation
	 */
	allocation_type?: string | null;
	/**
	 * Date when hire starts
	 */
	hirestartdate?: string | null;
	/**
	 * Date when hire ends
	 */
	hireenddate?: string | null;
	/**
	 * Status of the hire
	 */
	hirestatus?: string | null;
	/**
	 * Score of the claim
	 */
	claimscore?: number | null;
	/**
	 * CAPTCHA verification token
	 */
	captchaToken?: string | null;
	/**
	 * Whether to use CAPTCHA verification
	 */
	useCaptcha?: boolean;
	/**
	 * Notes about the source or origin
	 */
	sourceorignote?: string | null;
	/**
	 * Reference from the source
	 */
	source_ref?: string | null;
	/**
	 * Reason for pending status
	 */
	pending_reason?: string | null;
	/**
	 * Reason for rejection
	 */
	rejection_reason?: string | null;
	/**
	 * List of interested persons related to the request
	 */
	interested_persons?: Array<Interested_PersonBase> | null;
	/**
	 * Client's date of birth
	 */
	client_dob?: string | null;
	/**
	 * Client's occupation
	 */
	client_occupation?: string | null;
	/**
	 * Purpose of journey
	 */
	purpose_of_journey?: string | null;
	/**
	 * Pre-existing damage
	 */
	pre_existing_damage?: string | null;
	/**
	 * Description of client's damage
	 */
	client_damage_description?: string | null;
	/**
	 * Number of passengers Clients
	 */
	no_of_passengers_clients?: number | null;
	/**
	 * Client's speed
	 */
	client_speed?: string | null;
	/**
	 * Were there any witnesses
	 */
	any_witnesses?: boolean | null;
	/**
	 * Weather conditions
	 */
	weather_conditions?: string | null;
	/**
	 * Source of liability
	 */
	source_liability?: string | null;
	/**
	 * Emergency service attended
	 */
	emergency_service_attend?: boolean | null;
	/**
	 * Did police attend
	 */
	police_attended?: boolean | null;
	/**
	 * Did fire brigade attend
	 */
	fire_brigade_attended?: boolean | null;
	/**
	 * Did ambulance attend
	 */
	ambulance_attended?: boolean | null;
	/**
	 * Name of Police station
	 */
	police_station_name?: string | null;
	/**
	 * Name of Police officer
	 */
	police_officer_name?: string | null;
	/**
	 * Police Reference
	 */
	police_reference?: string | null;
	/**
	 * Policy Cover
	 */
	policy_cover?: string | null;
	/**
	 * Policy Excess
	 */
	policy_excess?: number | null;
	/**
	 * Policy Start Date
	 */
	policy_start_date?: string | null;
	/**
	 * Policy Expires Date
	 */
	policy_expiry_date?: string | null;
	/**
	 * VAT Registered
	 */
	vat_registered?: boolean | null;
	/**
	 * Policy vehicle use
	 */
	policy_vehicle_use?: string | null;
	/**
	 * Client Vehicle Drivable
	 */
	client_vehicle_drivable?: boolean | null;
	/**
	 * Client Vehicle Secure
	 */
	client_vehicle_secure?: boolean | null;
	/**
	 * Client Vehicle Location
	 */
	client_vehicle_location?: string | null;
	/**
	 * Flag to mark ACA Basic variant
	 */
	is_aca_basic?: boolean;
	/**
	 * Flag for ACA email imports - assign and send warm-transfer supplier email (no API)
	 */
	is_aca_import?: boolean;
};



export type RequestLogPublic = {
	method: string | null;
	path: string | null;
	query_params?: Record<string, unknown> | null;
	headers?: string | null;
	response_status?: number | null;
	user_id?: number | null;
	api_key: string | null;
	ip_address?: string | null;
	status_code?: number | null;
	request_body?: Record<string, unknown> | Array<unknown> | null;
	response_body?: Record<string, unknown> | Array<unknown> | null;
	path_params?: Record<string, unknown> | null;
	process_time?: number | null;
	billing_status?: string | null;
	normalized_endpoint: string | null;
	rate_limited?: boolean | null;
	duration_ms?: number | null;
	identifier_type: string | null;
	rate_limit_remaining?: number | null;
	endpoint: string | null;
	user_type: string | null;
	id: number;
	created_datetime: string;
	updated_datetime?: string | null;
};



export type RequestLogsPublic = {
	data: Array<RequestLogPublic>;
	count: number;
};



export type Role = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	role: string;
	role_description: string;
	id?: number | null;
};



export type RoleCreate = {
	role: string;
	role_description: string;
};



export type RolePublic = {
	role: string;
	role_description: string;
	id: number;
};



export type RoleUpdate = {
	role?: string | null;
	role_description?: string | null;
};



export type Role_AllocationCreate = {
	user_id?: number;
	role_id?: number;
};



export type Role_AllocationPublic = {
	user_id?: number;
	role_id?: number;
	id: number;
	user: User;
	role: Role;
};



export type Role_AllocationUpdate = {
	user_id?: number;
	role_id?: number;
};



export type Role_AllocationsPublic = {
	data: Array<Role_AllocationPublic>;
	count: number;
};



export type Role_PermissionCreate = {
	role_id?: number | null;
	endpoint: string;
	operation: string;
};



export type Role_PermissionPublic = {
	role_id?: number | null;
	endpoint: string;
	operation: string;
	id: number;
	role: Role;
};



export type Role_PermissionUpdate = {
	role_id?: number | null;
	endpoint?: string | null;
	operation?: string | null;
};



export type Role_PermissionsPublic = {
	data: Array<Role_PermissionPublic>;
	count: number;
};



export type RolesPublic = {
	data: Array<RolePublic>;
	count: number;
};



export type RunInspectorResponse = {
	referral_id: number;
	agent_runs: Array<AgentRunPublic>;
	claim_summaries: Array<ClaimSummaryDetailPublic>;
	workflow: app__api__routes__run_inspector__WorkflowDefinitionPublic | null;
};



/**
 * Model for creating new security test results.
 */
export type SecurityTestResultCreate = {
	/**
	 * Test suite name (authentication, authorization, etc.)
	 */
	testSuiteName: string;
	/**
	 * Individual test name
	 */
	testName: string;
	/**
	 * Test group for organizing related tests
	 */
	testGroup?: string | null;
	/**
	 * Test status: passed, failed, skipped, error
	 */
	status: string;
	/**
	 * Execution environment: local, staging, production
	 */
	environment: string;
	/**
	 * Test execution duration in milliseconds
	 */
	durationMs: number;
	/**
	 * Error message if test failed
	 */
	errorMessage?: string | null;
	/**
	 * Stack trace for debugging
	 */
	stackTrace?: string | null;
	/**
	 * When the test was executed
	 */
	executedAt?: string;
	/**
	 * Who executed the test (system or username)
	 */
	executedBy?: string;
	/**
	 * Additional metrics (coverage %, pass rate, etc.)
	 */
	metrics?: Record<string, unknown> | null;
};



/**
 * Public model for security test results.
 */
export type SecurityTestResultPublic = {
	/**
	 * Test suite name (authentication, authorization, etc.)
	 */
	testSuiteName: string;
	/**
	 * Individual test name
	 */
	testName: string;
	/**
	 * Test group for organizing related tests
	 */
	testGroup?: string | null;
	/**
	 * Test status: passed, failed, skipped, error
	 */
	status: string;
	/**
	 * Execution environment: local, staging, production
	 */
	environment: string;
	/**
	 * Test execution duration in milliseconds
	 */
	durationMs: number;
	/**
	 * Error message if test failed
	 */
	errorMessage?: string | null;
	/**
	 * Stack trace for debugging
	 */
	stackTrace?: string | null;
	/**
	 * When the test was executed
	 */
	executedAt?: string;
	/**
	 * Who executed the test (system or username)
	 */
	executedBy?: string;
	/**
	 * Additional metrics (coverage %, pass rate, etc.)
	 */
	metrics?: Record<string, unknown> | null;
	id: string;
	createdAt: string;
	updatedAt: string;
};



/**
 * Public model for list of security test results.
 */
export type SecurityTestResultsPublic = {
	data: Array<SecurityTestResultPublic>;
	count: number;
};



export type ServiceAccountCreateRequest = {
	email: string;
	fullName?: string | null;
	serviceAccountName?: string | null;
	brandAccess?: string;
	partnerId: number;
	roleIds?: Array<number>;
};



export type Source_RateCreate = {
	veh_group_id?: number | null;
	allocation_type?: string | null;
	rate?: number | null;
	source_partner_id?: number | null;
	tier?: string | null;
	start_date?: string | null;
	end_date?: string | null;
	status?: string | null;
	vehicle_type?: string | null;
};



export type Source_RatePublic = {
	veh_group_id?: number | null;
	allocation_type?: string | null;
	rate?: number | null;
	source_partner_id?: number | null;
	tier?: string | null;
	start_date?: string | null;
	end_date?: string | null;
	status?: string | null;
	id: number;
	source_partner?: Partner | null;
	veh_group?: Veh_Group | null;
};



export type Source_RateUpdate = {
	veh_group_id?: number | null;
	allocation_type?: string | null;
	rate?: number | null;
	source_partner_id?: number | null;
	tier?: string | null;
	start_date?: string | null;
	end_date?: string | null;
	status?: string | null;
};



export type Source_RatesPublic = {
	data: Array<Source_RatePublic>;
	count: number;
};



/**
 * Request body for in-app summary feedback submission.
 */
export type SummaryFeedbackBody = {
	rating: string;
	comment?: string | null;
	reason_code?: string | null;
	task_id?: string | null;
};



export type Supplier_RateCreate = {
	authority_id?: number | null;
	plate_type?: string | null;
	veh_group_id?: number | null;
	circs_grade_id?: number | null;
	type?: string | null;
	rate?: number | null;
	hire_length?: number | null;
	supplier_partner_id?: number | null;
	start_date?: string | null;
	end_date?: string | null;
	status?: string | null;
	source_partner_id?: number | null;
	rate_override?: string | null;
	vehicle_type?: string | null;
};



export type Supplier_RatePublic = {
	authority_id?: number | null;
	plate_type?: string | null;
	veh_group_id?: number | null;
	circs_grade_id?: number | null;
	type?: string | null;
	rate?: number | null;
	hire_length?: number | null;
	supplier_partner_id?: number | null;
	start_date?: string | null;
	end_date?: string | null;
	status?: string | null;
	source_partner_id?: number | null;
	rate_override?: string | null;
	id: number;
	supplier_partner: Partner | null;
	source_partner: Partner | null;
	veh_group: Veh_Group | null;
};



export type Supplier_RateUpdate = {
	authority_id?: number | null;
	plate_type?: string | null;
	veh_group_id?: number | null;
	circs_grade_id?: number | null;
	type?: string | null;
	rate?: number | null;
	hire_length?: number | null;
	supplier_partner_id?: number | null;
	start_date?: string | null;
	end_date?: string | null;
	status?: string | null;
	source_partner_id?: number | null;
	rate_override?: string | null;
	vehicle_type?: string | null;
};



export type Supplier_RatesPublic = {
	data: Array<Supplier_RatePublic>;
	count: number;
};



export type SwitchCompanyRequest = {
	tenant_id: string;
};



/**
 * Model for creating task schedule configuration.
 * 
 * SECURITY FIX (INJ-DOS-001): Added payload size limits to prevent DOS attacks.
 */
export type TaskScheduleConfigCreate = {
	/**
	 * Unique identifier for the task (e.g., "workflow_scheduled_audit")
	 */
	taskId: string;
	/**
	 * Display name for the task
	 */
	taskName: string;
	/**
	 * What the task does
	 */
	description?: string | null;
	/**
	 * Whether the task is currently active
	 */
	enabled?: boolean;
	/**
	 * Schedule status: active, disabled, deleted
	 */
	status?: string;
	/**
	 * Schedule type: "cron", "interval", or "time"
	 */
	scheduleType: string;
	/**
	 * JSON schedule config: {"hour": 15, "minute": 0} or {"seconds": 300} or "0 15 * * *"
	 */
	scheduleValue: string;
	/**
	 * Celery queue to route the task to
	 */
	queueName?: string;
	/**
	 * JSON array of email addresses
	 */
	emailRecipients?: string | null;
	/**
	 * JSON object with task-specific filter parameters
	 */
	filterCriteria?: string | null;
	/**
	 * JSON blob for output channel layout configuration (legacy)
	 */
	outputLayoutProfile?: Record<string, unknown> | null;
	/**
	 * FK to report_template — the shared template to use for output
	 */
	reportTemplateId?: number | null;
	/**
	 * Per-schedule overrides for subject_template, email_html, or pdf_html
	 */
	templateOverrides?: Record<string, unknown> | null;
	/**
	 * Workflow step ID of the merge bot (defaults to Sally scan when null)
	 */
	mergeBotStepId?: string | null;
	/**
	 * When true, run batch urgency rank agent after workflow batch completes (before report CSV/PDF/email).
	 */
	batchRankingEnabled?: boolean;
	/**
	 * When the task last executed
	 */
	lastRunDatetime?: string | null;
	/**
	 * When the task is scheduled to run next
	 */
	nextRunDatetime?: string | null;
	/**
	 * Total number of executions
	 */
	executionCount?: number;
	/**
	 * Total number of failed executions
	 */
	failureCount?: number;
};



/**
 * Public model for task schedule configuration.
 */
export type TaskScheduleConfigPublic = {
	/**
	 * Unique identifier for the task (e.g., "workflow_scheduled_audit")
	 */
	taskId: string;
	/**
	 * Display name for the task
	 */
	taskName: string;
	/**
	 * What the task does
	 */
	description?: string | null;
	/**
	 * Whether the task is currently active
	 */
	enabled?: boolean;
	/**
	 * Schedule status: active, disabled, deleted
	 */
	status?: string;
	/**
	 * Schedule type: "cron", "interval", or "time"
	 */
	scheduleType: string;
	/**
	 * JSON schedule config: {"hour": 15, "minute": 0} or {"seconds": 300} or "0 15 * * *"
	 */
	scheduleValue: string;
	/**
	 * Celery queue to route the task to
	 */
	queueName?: string;
	/**
	 * JSON array of email addresses
	 */
	emailRecipients?: string | null;
	/**
	 * JSON object with task-specific filter parameters
	 */
	filterCriteria?: string | null;
	/**
	 * JSON blob for output channel layout configuration (legacy)
	 */
	outputLayoutProfile?: Record<string, unknown> | null;
	/**
	 * FK to report_template — the shared template to use for output
	 */
	reportTemplateId?: number | null;
	/**
	 * Per-schedule overrides for subject_template, email_html, or pdf_html
	 */
	templateOverrides?: Record<string, unknown> | null;
	/**
	 * Workflow step ID of the merge bot (defaults to Sally scan when null)
	 */
	mergeBotStepId?: string | null;
	/**
	 * When true, run batch urgency rank agent after workflow batch completes (before report CSV/PDF/email).
	 */
	batchRankingEnabled?: boolean;
	/**
	 * When the task last executed
	 */
	lastRunDatetime?: string | null;
	/**
	 * When the task is scheduled to run next
	 */
	nextRunDatetime?: string | null;
	/**
	 * Total number of executions
	 */
	executionCount?: number;
	/**
	 * Total number of failed executions
	 */
	failureCount?: number;
	id: number;
	createdDatetime: string;
	updatedDatetime?: string | null;
};



/**
 * Model for updating task schedule configuration.
 * 
 * SECURITY FIX (INJ-DOS-001): Added payload size limits to prevent DOS attacks.
 */
export type TaskScheduleConfigUpdate = {
	taskName?: string | null;
	description?: string | null;
	enabled?: boolean | null;
	status?: string | null;
	scheduleType?: string | null;
	scheduleValue?: string | null;
	queueName?: string | null;
	emailRecipients?: string | null;
	filterCriteria?: string | null;
	outputLayoutProfile?: Record<string, unknown> | null;
	reportTemplateId?: number | null;
	templateOverrides?: Record<string, unknown> | null;
	mergeBotStepId?: string | null;
	batchRankingEnabled?: boolean | null;
	lastRunDatetime?: string | null;
	nextRunDatetime?: string | null;
	executionCount?: number | null;
	failureCount?: number | null;
};



/**
 * Public model for list of task schedule configurations.
 */
export type TaskScheduleConfigsPublic = {
	data: Array<TaskScheduleConfigPublic>;
	count: number;
};



/**
 * Public model for task execution history.
 */
export type TaskScheduleExecutionPublic = {
	/**
	 * Foreign key to TaskScheduleConfig
	 */
	taskScheduleId: number;
	/**
	 * When the task should have run (UTC)
	 */
	scheduledTime: string;
	/**
	 * When execution actually started (UTC)
	 */
	actualStartTime?: string | null;
	/**
	 * When execution completed (UTC)
	 */
	actualEndTime?: string | null;
	/**
	 * Execution duration in seconds
	 */
	durationSeconds?: number | null;
	/**
	 * Execution status: pending, running, success, failed
	 */
	status: string;
	/**
	 * Error message if execution failed
	 */
	errorMessage?: string | null;
	/**
	 * Number of records processed (task-specific)
	 */
	rowsProcessed?: number | null;
	id: number;
	createdDatetime: string;
	updatedDatetime?: string | null;
};



/**
 * Public model for list of task executions.
 */
export type TaskScheduleExecutionsPublic = {
	data: Array<TaskScheduleExecutionPublic>;
	count: number;
};



export type TestRequest = {
	referral_id: number;
};



export type UpdatePassword = {
	currentPassword: string;
	newPassword: string;
};



export type Upload = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	referral_id?: number;
	file_name: string;
	file_path: string;
	created_by?: number | null;
	id?: number | null;
};



export type User = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	email: string;
	isActive?: boolean;
	isSuperuser?: boolean;
	fullName?: string | null;
	effectivePartnerType?: string | null;
	/**
	 * wysehub | aiclaims | both
	 */
	brandAccess?: string;
	/**
	 * human | service
	 */
	accountType?: string;
	canPortalLogin?: boolean;
	serviceAccountName?: string | null;
	id?: number | null;
	hashed_password: string;
	requiresOtp?: boolean;
	locked?: boolean;
	managedByUserId?: number | null;
};



export type UserCreate = {
	email: string;
	isActive?: boolean;
	isSuperuser?: boolean;
	fullName?: string | null;
	effectivePartnerType?: string | null;
	/**
	 * wysehub | aiclaims | both
	 */
	brandAccess?: string;
	/**
	 * human | service
	 */
	accountType?: string;
	canPortalLogin?: boolean;
	serviceAccountName?: string | null;
	password: string;
	requiresOtp?: boolean;
};



export type UserPublic = {
	email: string;
	isActive?: boolean;
	isSuperuser?: boolean;
	fullName?: string | null;
	effectivePartnerType?: string | null;
	/**
	 * wysehub | aiclaims | both
	 */
	brandAccess?: string;
	/**
	 * human | service
	 */
	accountType?: string;
	canPortalLogin?: boolean;
	serviceAccountName?: string | null;
	id: number;
	rolenames: Array<string>;
	partnerTypes: Array<string>;
	/**
	 * Resolved wysehub | aiclaims for this request (not persisted)
	 */
	activeBrand?: string | null;
};



export type UserPublicBasic = {
	email: string;
	isActive?: boolean;
	isSuperuser?: boolean;
	fullName?: string | null;
	effectivePartnerType?: string | null;
	/**
	 * wysehub | aiclaims | both
	 */
	brandAccess?: string;
	/**
	 * human | service
	 */
	accountType?: string;
	canPortalLogin?: boolean;
	serviceAccountName?: string | null;
	id: number;
};



export type UserRegister = {
	email: string;
	password: string;
	fullName?: string | null;
};



export type UserUpdate = {
	email?: string | null;
	isActive?: boolean | null;
	fullName?: string | null;
	effectivePartnerType?: string | null;
	brandAccess?: string | null;
	password?: string | null;
};



export type UserUpdateMe = {
	fullName?: string | null;
	email?: string | null;
	effectivePartnerType?: string | null;
};



export type User_PartnerCreate = {
	partner_id?: number;
	user_id?: number;
};



export type User_PartnerPublic = {
	partner_id?: number;
	user_id?: number;
	id: number;
	partner: Partner;
	user: User;
};



export type User_PartnerUpdate = {
	partner_id?: number | null;
	user_id?: number | null;
};



export type User_PartnersPublic = {
	data: Array<User_PartnerPublic>;
	count: number;
};



export type UsersPublic = {
	data: Array<UserPublic>;
	count: number;
};



export type ValidationError = {
	loc: Array<string | number>;
	msg: string;
	type: string;
	input?: unknown;
	ctx?: Record<string, unknown>;
};



export type Veh_Group = {
	createdDatetime?: string | null;
	updatedDatetime?: string | null;
	group?: string | null;
	desc?: string | null;
	type?: string | null;
	id?: number | null;
};



export type Veh_GroupCreate = {
	group?: string | null;
	desc?: string | null;
	type?: string | null;
};



export type Veh_GroupPublic = {
	group?: string | null;
	desc?: string | null;
	type?: string | null;
	id: number;
};



export type Veh_GroupUpdate = {
	group?: string | null;
	desc?: string | null;
	type?: string | null;
};



export type Veh_GroupsPublic = {
	data: Array<Veh_GroupPublic>;
	count: number;
};



export type WorkflowBillingEventPublic = {
	/**
	 * Billing partner. NULL for default/unresolved partner runs.
	 */
	partnerId?: number | null;
	/**
	 * e.g. 'matt', 'emma', 'audrey', 'annie'
	 */
	workflowKey: string;
	referralId?: number | null;
	/**
	 * Links to the group of agent_runs for this workflow run.
	 */
	taskId?: string | null;
	/**
	 * 'workflow_run' for agent runs.
	 */
	billingType: string;
	/**
	 * First day of the billing month (YYYY-MM-01).
	 */
	billingPeriod: string;
	/**
	 * 'pending' | 'billed' | 'excluded' | 'capped'
	 */
	status?: string;
	/**
	 * Set when the event is included on an invoice.
	 */
	invoiceId?: number | null;
	/**
	 * When the workflow run was dispatched.
	 */
	startedAt: string;
	/**
	 * True for test/evaluation runs that should not be billed.
	 */
	evaluationMode?: boolean;
	id: number;
	created_datetime: string;
	updated_datetime: string | null;
};



export type WorkflowBillingEventUpdate = {
	status?: string | null;
	invoiceId?: number | null;
};



export type WorkflowBillingEventsPublic = {
	data: Array<WorkflowBillingEventPublic>;
	count: number;
};



export type WorkflowCardPublic = {
	displayName: string;
	workflowKey: string;
	tenantId: string;
	workflowId?: number | null;
	severity: 'ok' | 'warn' | 'err';
	statusLine: string;
	lastRunAt?: string | null;
	runsInPeriod: number;
	inputTokens?: number | null;
	outputTokens?: number | null;
	cacheCreationInputTokens?: number | null;
	cacheReadInputTokens?: number | null;
	periodCost?: number | null;
};




export type WorkflowCardsPublic = {
	data: Array<WorkflowCardPublic>;
};



export type WorkflowListResponse = {
	data: Array<app__api__routes__workflow_definitions__WorkflowDefinitionPublic>;
};



export type WorkflowRateConfigCreate = {
	partnerId: number;
	/**
	 * Workflow identifier (e.g., 'matt', 'emma', 'audrey', 'annie')
	 */
	workflowKey: string;
	isActive?: boolean;
	/**
	 * Cost per workflow run
	 */
	ratePerRun: number;
	/**
	 * Minimum billable runs per month (for tiered pricing)
	 */
	minRunsPerMonth?: number | null;
	/**
	 * Maximum runs per month before additional rate applies
	 */
	maxRunsPerMonth?: number | null;
	/**
	 * Rate for runs exceeding max_runs_per_month
	 */
	overageRate?: number | null;
	/**
	 * Type of volume discount ('percentage', 'fixed_price', None for none)
	 */
	volumeDiscountType?: string | null;
	/**
	 * Number of runs needed to qualify for volume discount
	 */
	volumeDiscountThreshold?: number | null;
	/**
	 * Discount value (percentage 0-100 or fixed price)
	 */
	volumeDiscountValue?: number | null;
	/**
	 * Date when this rate becomes effective
	 */
	effectiveFrom?: string | null;
	/**
	 * Date when this rate expires (NULL = indefinite)
	 */
	effectiveTo?: string | null;
};



export type WorkflowRateConfigPublic = {
	partnerId: number;
	/**
	 * Workflow identifier (e.g., 'matt', 'emma', 'audrey', 'annie')
	 */
	workflowKey: string;
	isActive?: boolean;
	/**
	 * Cost per workflow run
	 */
	ratePerRun: number;
	/**
	 * Minimum billable runs per month (for tiered pricing)
	 */
	minRunsPerMonth?: number | null;
	/**
	 * Maximum runs per month before additional rate applies
	 */
	maxRunsPerMonth?: number | null;
	/**
	 * Rate for runs exceeding max_runs_per_month
	 */
	overageRate?: number | null;
	/**
	 * Type of volume discount ('percentage', 'fixed_price', None for none)
	 */
	volumeDiscountType?: string | null;
	/**
	 * Number of runs needed to qualify for volume discount
	 */
	volumeDiscountThreshold?: number | null;
	/**
	 * Discount value (percentage 0-100 or fixed price)
	 */
	volumeDiscountValue?: number | null;
	/**
	 * Date when this rate becomes effective
	 */
	effectiveFrom?: string | null;
	/**
	 * Date when this rate expires (NULL = indefinite)
	 */
	effectiveTo?: string | null;
	id: number;
	created_datetime: string;
	updated_datetime: string | null;
};



export type WorkflowRateConfigUpdate = {
	isActive?: boolean | null;
	ratePerRun?: number | null;
	minRunsPerMonth?: number | null;
	maxRunsPerMonth?: number | null;
	overageRate?: number | null;
	volumeDiscountType?: string | null;
	volumeDiscountThreshold?: number | null;
	volumeDiscountValue?: number | null;
	effectiveFrom?: string | null;
	effectiveTo?: string | null;
};



export type WorkflowRateConfigsPublic = {
	data: Array<WorkflowRateConfigPublic>;
	count: number;
};



export type WorkflowRunDetailPublic = {
	taskId: string;
	grafanaBaseUrl?: string | null;
	agent_runs: Array<AgentRunPublic>;
	feedback_requests: Array<FeedbackRequestPublic>;
	feedback_responses: Array<FeedbackResponsePublic>;
};



export type WorkflowRunSummaryPublic = {
	taskId: string;
	lastRunAt: string;
	stepCount: number;
	referralId?: number | null;
	primaryAgentName?: string | null;
	runType?: string | null;
	traceId?: string | null;
	feedbackUp: number;
	feedbackDown: number;
	feedbackPendingSync: number;
	feedbackSyncErrors: number;
};



export type WorkflowRunsListPublic = {
	data: Array<WorkflowRunSummaryPublic>;
	count: number;
};



export type WorkflowSpec = {
	partner_id?: number | null;
	workflow_key: string;
	status?: string;
};



export type XeroSeedRequest = {
	refresh_token: string;
	tenant_id: string;
};



export type app__api__routes__run_inspector__WorkflowDefinitionPublic = {
	id: number;
	partner_id: number | null;
	workflow_key: string;
	display_name: string | null;
	version: number;
	definition: Record<string, unknown>;
	status: string;
	description: string | null;
};



/**
 * API shape for workflow rows; *definition* is always a JSON object for clients.
 */
export type app__api__routes__workflow_definitions__WorkflowDefinitionPublic = {
	id: number;
	partner_id: number | null;
	workflow_key: string;
	display_name: string | null;
	version: number;
	definition?: Record<string, unknown>;
	status: string;
	description: string | null;
	created_datetime: string | null;
	updated_datetime: string | null;
};

